import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Award, Lock } from "lucide-react";
import {
  TIERED_BADGES,
  SPECIAL_BADGES,
  TIER_META,
  TIER_ORDER,
} from "@/lib/achievementsData";

export default function BadgesList() {
  const [view, setView] = useState("tiered"); // 'tiered' | 'special'
  const totalCount = useMemo(
    () => TIERED_BADGES.length * TIER_ORDER.length + SPECIAL_BADGES.length,
    []
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="badges-list-page">
      {/* Header */}
      <div className="mb-8 md:mb-12">
        <Link
          to="/wiki"
          className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-3"
          data-testid="badges-back-to-wiki"
        >
          <ChevronLeft className="w-3 h-3" /> Wiki
        </Link>
        <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">
          // Honors Hall
        </span>
        <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase leading-tight">
          Badges &amp; Achievements
        </h1>
        <p className="text-sm text-[var(--t-text-dim)] mt-2">
          {totalCount} commendations · earned through service to your nation
        </p>
      </div>

      {/* View toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="badges-view-toggle">
        <ViewTab
          label="Tiered Medals"
          count={TIERED_BADGES.length * TIER_ORDER.length}
          active={view === "tiered"}
          onClick={() => setView("tiered")}
          testid="view-tiered"
        />
        <ViewTab
          label="Special Achievements"
          count={SPECIAL_BADGES.length}
          active={view === "special"}
          onClick={() => setView("special")}
          testid="view-special"
        />
      </div>

      {view === "tiered" ? <TieredSection /> : <SpecialSection />}
    </div>
  );
}

function ViewTab({ label, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative px-4 py-2.5 text-[11px] md:text-xs font-mono tracking-[0.2em] uppercase border transition group ${
        active
          ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
          : "border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:border-[var(--t-border-hi)]"
      }`}
    >
      {label}
      <span
        className={`ml-2 text-[9px] tracking-normal ${
          active ? "text-[var(--t-blue-glow)]" : "text-[var(--t-text-muted)]"
        }`}
      >
        [{count}]
      </span>
      {active && (
        <span className="absolute left-0 right-0 -bottom-px h-px bg-[var(--t-cyan)] shadow-[0_0_10px_var(--t-cyan)]" />
      )}
    </button>
  );
}

// =====================================================
// TIERED SECTION — one row per badge family with 5 medals
// =====================================================
function TieredSection() {
  return (
    <div className="space-y-5" data-testid="tiered-badges-section">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 panel" data-testid="tier-legend">
        <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mr-2">
          // Tier ladder
        </span>
        {TIER_ORDER.map((t) => {
          const meta = TIER_META[t];
          return (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-[0.15em] uppercase"
              style={{ color: meta.color }}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: meta.color, boxShadow: `0 0 8px ${meta.glow}` }}
              />
              {t}
            </span>
          );
        })}
      </div>

      {TIERED_BADGES.map((b) => (
        <TieredRow key={b.slug} badge={b} />
      ))}
    </div>
  );
}

function TieredRow({ badge }) {
  const Icon = badge.icon;
  return (
    <div
      className="panel overflow-hidden"
      data-testid={`tiered-row-${badge.slug}`}
    >
      <div className="grid lg:grid-cols-[280px_1fr]">
        {/* Left: badge identity */}
        <div className="p-5 md:p-6 border-b lg:border-b-0 lg:border-r border-[var(--t-border)] bg-gradient-to-br from-[rgba(58,160,255,0.04)] to-transparent">
          <div className="flex items-start gap-4">
            <div
              className="relative w-14 h-14 md:w-16 md:h-16 flex-shrink-0 flex items-center justify-center border border-[var(--t-border-hi)] bg-[#050912]"
              style={{
                boxShadow: "inset 0 0 20px rgba(58,160,255,0.08), 0 0 24px rgba(58,160,255,0.10)",
              }}
            >
              <Icon className="w-7 h-7 md:w-8 md:h-8 text-[var(--t-cyan)]" strokeWidth={1.4} />
              <span className="absolute -top-px -left-px w-2 h-2 border-t border-l border-[var(--t-cyan)]" />
              <span className="absolute -bottom-px -right-px w-2 h-2 border-b border-r border-[var(--t-cyan)]" />
            </div>
            <div className="min-w-0">
              <span className="font-mono text-[9px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">
                {badge.category}
              </span>
              <h3 className="font-display text-xl md:text-2xl uppercase text-[var(--t-text)] leading-tight mt-1">
                {badge.name}
              </h3>
              <p className="text-xs md:text-sm text-[var(--t-text-dim)] mt-2">{badge.description}</p>
            </div>
          </div>
        </div>

        {/* Right: 5 tier medals */}
        <div className="p-4 md:p-5">
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="font-mono text-[9px] md:text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase">
              // {badge.metric}
            </span>
            <span className="font-mono text-[9px] md:text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase hidden md:inline">
              progression →
            </span>
          </div>
          <div className="grid grid-cols-5 gap-2 md:gap-3">
            {badge.tiers.map((t, idx) => (
              <TierMedal key={t.tier} tier={t.tier} label={t.label} index={idx} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TierMedal({ tier, label, index }) {
  const meta = TIER_META[tier];
  return (
    <div
      className="group relative flex flex-col items-center text-center"
      data-testid={`tier-medal-${tier.toLowerCase()}`}
    >
      {/* Connector line on the left (except first) */}
      {index > 0 && (
        <span
          className="hidden md:block absolute -left-1.5 top-7 w-3 h-px"
          style={{
            background: `linear-gradient(to right, transparent, ${meta.color}55)`,
          }}
        />
      )}
      <div
        className="relative w-12 h-12 md:w-14 md:h-14 flex items-center justify-center border transition group-hover:scale-105"
        style={{
          borderColor: meta.color,
          background: `radial-gradient(circle at 50% 30%, ${meta.soft}, transparent 70%), #050912`,
          boxShadow: `inset 0 0 18px ${meta.soft}, 0 0 12px ${meta.glow}`,
        }}
      >
        <span
          className="font-display text-base md:text-lg uppercase leading-none"
          style={{ color: meta.color, textShadow: `0 0 10px ${meta.glow}` }}
        >
          {tier.slice(0, 1)}
        </span>
      </div>
      <span
        className="mt-2 font-mono text-[9px] md:text-[10px] tracking-[0.15em] uppercase"
        style={{ color: meta.color }}
      >
        {tier}
      </span>
      <span className="mt-0.5 font-mono text-[10px] md:text-xs text-[var(--t-text)] leading-tight break-words px-0.5">
        {label}
      </span>
    </div>
  );
}

// =====================================================
// SPECIAL SECTION — one-off insignias
// =====================================================
function SpecialSection() {
  return (
    <div data-testid="special-badges-section">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 panel mb-5">
        <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">
          // One-off commendations
        </span>
        <span className="text-[11px] text-[var(--t-text-dim)]">
          Awarded for unique events. Cannot be ranked up.
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SPECIAL_BADGES.map((b) => (
          <SpecialCard key={b.slug} badge={b} />
        ))}
      </div>
    </div>
  );
}

function SpecialCard({ badge }) {
  const Icon = badge.icon;
  const accent = badge.accent || "var(--t-cyan)";
  return (
    <div
      className="group relative panel overflow-hidden p-5 hover:border-[var(--t-blue)] transition"
      data-testid={`special-badge-${badge.slug}`}
    >
      {/* Corner brackets */}
      <span className="absolute top-0 left-0 w-3 h-3 border-t border-l" style={{ borderColor: accent }} />
      <span className="absolute top-0 right-0 w-3 h-3 border-t border-r" style={{ borderColor: accent }} />
      <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l" style={{ borderColor: accent }} />
      <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r" style={{ borderColor: accent }} />

      <div className="flex items-start gap-4">
        <div
          className="relative w-14 h-14 flex-shrink-0 flex items-center justify-center"
          style={{
            background: `radial-gradient(circle at 50% 35%, ${hexToRgba(accent, 0.18)}, transparent 70%), #050912`,
            border: `1px solid ${accent}`,
            boxShadow: `0 0 18px ${hexToRgba(accent, 0.35)}, inset 0 0 18px ${hexToRgba(accent, 0.10)}`,
          }}
        >
          <Icon
            className="w-7 h-7 transition group-hover:scale-110"
            style={{ color: accent, filter: `drop-shadow(0 0 8px ${hexToRgba(accent, 0.5)})` }}
            strokeWidth={1.5}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3
              className="font-display text-lg uppercase leading-tight"
              style={{ color: accent, textShadow: `0 0 14px ${hexToRgba(accent, 0.45)}` }}
            >
              {badge.name}
            </h3>
            <Lock className="w-3 h-3 text-[var(--t-text-muted)] flex-shrink-0" />
          </div>
          <p className="text-xs text-[var(--t-text-dim)] leading-relaxed">{badge.description}</p>
        </div>
      </div>
    </div>
  );
}

// Helpers
function hexToRgba(hex, alpha = 1) {
  if (!hex || hex.startsWith("var(")) return `rgba(58,160,255,${alpha})`;
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Legacy detail route — kept as a courtesy redirect to the main page
export function BadgeDetail() {
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 text-center" data-testid="badge-detail-page">
      <Award className="w-12 h-12 text-[var(--t-text-muted)] mx-auto mb-4" />
      <h1 className="font-display text-2xl md:text-3xl text-[var(--t-cyan)] uppercase mb-2">
        Honors Hall Redesigned
      </h1>
      <p className="text-sm text-[var(--t-text-dim)] mb-6">
        All badges &amp; achievements are now displayed in a single ladder view.
      </p>
      <Link to="/wiki/badges" className="btn-primary inline-flex" data-testid="badge-detail-goto-list">
        View Honors Hall
      </Link>
    </div>
  );
}
